import { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { EligibilityPill } from "@/components/EligibilityPill";
import { MatchScore } from "@/components/MatchScore";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import ChipsInput from "@/components/ChipsInput";
import { SearchPlanCard, MatchFunnel } from "@/components/OrchestratorPanel";
import { Sparkle, ArrowLeft, PaperPlaneTilt, WarningDiamond, UsersThree } from "@phosphor-icons/react";

export default function RecruiterWorkspace() {
  const { user } = useAuth();
  const [text, setText] = useState("");
  const [criteria, setCriteria] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pools, setPools] = useState([]);
  const [selPool, setSelPool] = useState("");
  useEffect(() => { api.get("/recruiter/pools").then((r) => setPools(r.data.pools || [])).catch(() => {}); }, []);
  const addToPool = async (c) => {
    if (!selPool) { toast.error("Pick a pool first."); return; }
    try { await api.post(`/recruiter/pools/${selPool}/candidates`, { candidate_id: c.candidate_id }); toast.success(`Added ${c.name} to pool`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  if (user && user.role !== "recruiter" && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const parse = async () => {
    setLoading(true);
    try { const { data } = await api.post("/recruiter/parse-role", { text }); setCriteria(data.criteria); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const upd = (k, v) => setCriteria((p) => ({ ...p, [k]: v }));

  const run = async () => {
    setLoading(true);
    try { const { data } = await api.post("/recruiter/shortlist", criteria); setResult(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const invite = async (c) => {
    try { await api.post("/b2b/invite", { candidate_id: c.candidate_id, role_title: criteria?.title, type: "invite_to_apply" });
      toast.success(`Invite sent to ${c.name}`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Candidate matching" subtitle="Describe a client role in plain words. The engine structures the criteria (you confirm), then automatically builds a candidate search, filters by real eligibility, and shortlists opt-in candidates — with reasons." />

      {!criteria && (
        <div className="rounded-lg border border-slate-200 bg-white p-6 max-w-2xl">
          <Label>Describe the role</Label>
          <Textarea rows={6} className="mt-1.5" value={text} onChange={(e) => setText(e.target.value)}
            placeholder="e.g. Remote research/writing role for a client, must be able to work from anywhere with no relocation, English C1, 3+ years experience." data-testid="role-text-input" />
          <Button onClick={parse} disabled={loading || !text.trim()} data-testid="parse-role-btn" className="mt-4 bg-slate-900 hover:bg-slate-800 gap-2">
            <Sparkle weight="fill" /> {loading ? "Structuring…" : "Structure criteria"}
          </Button>
        </div>
      )}

      {criteria && !result && (
        <div className="rounded-lg border border-slate-200 bg-white p-6 max-w-2xl" data-testid="criteria-review">
          <button onClick={() => setCriteria(null)} className="flex items-center gap-1 text-sm text-slate-500 mb-4"><ArrowLeft /> Rewrite</button>
          <p className="text-sm text-slate-600 mb-4">Confirm the extracted criteria (nothing invented — unknown fields stay empty).</p>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Title</Label><Input className="mt-1" value={criteria.title || ""} onChange={(e) => upd("title", e.target.value)} /></div>
            <div><Label className="text-xs">Work arrangement</Label><Input className="mt-1" value={criteria.work_arrangement || ""} onChange={(e) => upd("work_arrangement", e.target.value)} /></div>
          </div>
          <div className="mt-3"><Label className="text-xs">Remote from countries</Label><ChipsInput value={criteria.remote_countries || []} onChange={(v) => upd("remote_countries", v)} /></div>
          <div className="mt-3"><Label className="text-xs">Mandatory requirements</Label><ChipsInput value={criteria.mandatory_qualifications || []} onChange={(v) => upd("mandatory_qualifications", v)} /></div>
          <div className="mt-3"><Label className="text-xs">Preferred requirements</Label><ChipsInput value={criteria.preferred_qualifications || []} onChange={(v) => upd("preferred_qualifications", v)} /></div>
          <Button onClick={run} disabled={loading} data-testid="run-shortlist-btn" className="mt-5 bg-slate-900 hover:bg-slate-800">{loading ? "Matching…" : "Build shortlist"}</Button>
        </div>
      )}

      {result && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-slate-600">{result.candidates.length} of {result.pool_size} opt-in candidates qualify</p>
            <div className="flex items-center gap-2">
              {pools.length > 0 && (
                <select value={selPool} onChange={(e) => setSelPool(e.target.value)} data-testid="shortlist-pool-select"
                  className="rounded-md border border-slate-200 px-2 py-1.5 text-xs">
                  <option value="">Add to pool…</option>
                  {pools.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              )}
              <Button variant="ghost" onClick={() => { setResult(null); setCriteria(null); setText(""); }} className="text-slate-500">New search</Button>
            </div>
          </div>
          {result.contradictions?.length > 0 && (
            <div className="mb-3 rounded-md border border-orange-200 bg-orange-50 p-3 text-sm text-orange-800 flex items-start gap-2"><WarningDiamond weight="fill" className="text-orange-500 mt-0.5" /> {result.contradictions.map((c) => c.message).join(" ")}</div>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-1 space-y-4">
            <SearchPlanCard plan={result.plan} kind="candidates" />
            <MatchFunnel funnel={result.funnel} kind="candidates" />
          </div>
          <div className="lg:col-span-2 space-y-3" data-testid="shortlist">
            {result.candidates.map((c) => (
              <div key={c.candidate_id} className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`shortlist-${c.candidate_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div><p className="font-semibold text-slate-900">{c.name} <span className="text-xs font-normal text-slate-400">· {c.country || "—"}</span></p>
                    <p className="text-xs text-slate-500">{c.headline || "Candidate"}</p></div>
                  <div className="text-right"><EligibilityPill status={c.eligibility_status} /><div className="mt-1"><MatchScore score={c.match_score} /></div></div>
                </div>
                {c.strong_matches?.length > 0 && <p className="mt-2 text-xs text-emerald-700">✓ {c.strong_matches.map((m) => m.text).join(", ")}</p>}
                {c.flex_used?.length > 0 && <p className="mt-1 text-xs text-blue-700" data-testid="rec-flex-used">⇄ Flexibility used: {c.flex_used.map((f) => f.area).join(", ")}</p>}
                {c.gaps?.length > 0 && <p className="mt-1 text-xs text-slate-500">Gaps: {c.gaps.map((m) => m.text).join(", ")}</p>}
                <Button size="sm" onClick={() => invite(c)} data-testid={`rec-invite-${c.candidate_id}`} className="mt-3 bg-slate-900 hover:bg-slate-800 gap-1"><PaperPlaneTilt /> Invite to apply</Button>
                {pools.length > 0 && selPool && (
                  <Button size="sm" variant="outline" onClick={() => addToPool(c)} data-testid={`rec-addpool-${c.candidate_id}`} className="mt-3 ml-2 gap-1"><UsersThree /> Add to pool</Button>
                )}
              </div>
            ))}
          </div>
          </div>
        </div>
      )}
    </div>
  );
}
